<template>
  <div class="case-build-assistant container" :class="{'steps-collapsed': !state.stepsOpen}">
    <div v-show="state.stepsOpen" class="steps-container">
      <menu-button class="back-button" label="Back to selection" icon="arrow_back" @click="close" />
      <case-summary
        :name="state.caseConf.name"
        :avatar="state.caseConf._avatar"
        :environments="environments"
      />
      <div class="steps-menu-buttons" :active="state.activeStep">
        <menu-button
          v-for="(step, index) in stepsWithErrors"
          :key="step.key"
          class="step"
          :ai-icon="hasAiIcon(step.key)"
          :label="step.label | translateFeatureLabel"
          :style="{ 'background-color': step.color }"
          :class="{ active: index === state.activeStep }"
          @click.native="navigateToStep(index)"
        />
      </div>
      <timeline
        :steps="stepsWithErrors"
        :completed-steps-count="state.caseConf.completedStepsCount"
        :active-step="state.activeStep"
        @step="index => navigateToStep(index)"
      />
      <!-- Use key to prevent Vue from using the same component for both intances -->
      <div class="bottom-left-buttons">
        <md-button
          class="vrp-pill-button help-button"
          @click="showHelp"
        >
          <md-icon>help</md-icon>
          Help
        </md-button>

        <div v-if="isAdminOrInstructor">
          <md-button
            v-if="!isActiveStep('caseLogic')"
            key="SCENARIO_BUILDER"
            class="vrp-pill-button scenario-builder-button"
            @click="openTutorialDrawer('SCENARIO_BUILDER')"
          >
            Video Tutorials
          </md-button>
          <md-button
            v-if="isActiveStep('caseLogic')"
            key="SCENARIO_LOGIC"
            class="vrp-pill-button scenario-logic-button"
            @click="openTutorialDrawer('SCENARIO_LOGIC')"
          >
            Video Tutorials
          </md-button>
        </div>
      </div>
    </div>
    <div class="editor-container">
      <!-- Don't display the editor panels if the case was not loaded, ignore for new cases -->
      <div
        v-show="(state.isNewInitial || caseConf) && isInCaseEditor()"
        :class="{ editor: true, 'old-globals': !isActiveStep('medications') && !isActiveStep('dialogue') && !isActiveStep('customMenus') }"
      >
        <case-scenario
          v-show="isActiveStep('scenario')"
          v-if="hasLoadedStep('scenario')"
          :is-active="isActiveStep('scenario')"
          :case-conf="state.caseConf"
          :readonly="!canWrite"
          @update="updateCase"
        />
        <case-dispatch
          v-show="isActiveStep('dispatch')"
          v-if="hasLoadedStep('dispatch')"
          :case-conf="state.caseConf"
          :readonly="!canWrite"
          @update="updateCase"
        />
        <case-initial-conditions
          v-if="state.caseConf._avatar"
          v-show="isActiveStep('patientConditions')"
          :case-conf="state.caseConf"
          :readonly="!canWrite"
          @update="updateCase"
        />
        <div
          v-show="isActiveStep('dialogue')"
          v-if="hasLoadedStep('dialogue')"
          class="questions-container"
        >
          <case-questions-container
            :is-new-case="true"
            :case-conf="state.caseConf"
            :readonly="!canWrite"
            :navigation="state.navigation"
            @update="updateCase"
          />
        </div>
        <case-custom-menus
          v-show="isActiveStep('customMenus')"
          v-if="hasLoadedStep('customMenus')"
          :case-conf="state.caseConf"
          :readonly="!canWrite"
          @update="updateCase"
        />
        <case-ivs
          v-show="isActiveStep('ivs')"
          v-if="hasLoadedStep('ivs')"
          :case-conf="state.caseConf"
          :readonly="!canWrite"
          :navigation="state.navigation"
          @update="updateCase"
        />
        <case-medications
          v-show="isActiveStep('medications')"
          v-if="hasLoadedStep('medications')"
          :case-conf="state.caseConf"
          :readonly="!canWrite"
          :navigation="state.navigation"
          @update="updateCase"
          @saving="subPanelSaving"
          @saved="subPanelSaved"
        />
        <case-oxygens
          v-show="isActiveStep('oxygen')"
          v-if="hasLoadedStep('oxygen')"
          :case-conf="state.caseConf"
          :readonly="!canWrite"
          @update="updateCase"
        />
        <case-interventions
          v-show="isActiveStep('interventions')"
          v-if="hasLoadedStep('interventions')"
          :case-conf="state.caseConf"
          :readonly="!canWrite"
          @update="updateCase"
        />
        <case-procedures
          v-show="isActiveStep('procedures')"
          v-if="hasLoadedStep('procedures')"
          :case-conf="state.caseConf"
          :readonly="!canWrite"
          @update="updateCase"
        />
        <case-monitors
          v-show="isActiveStep('monitors')"
          v-if="hasLoadedStep('monitors')"
          :case-conf="state.caseConf"
          :readonly="!canWrite"
          @update="updateCase"
        />
        <case-lab-media
          v-show="isActiveStep('labMedia')"
          v-if="hasLoadedStep('labMedia')"
          :case-conf="state.caseConf"
          :warnings="warnings && warnings._labAssets"
          :readonly="!canWrite"
          @update="updateCase"
        />
        <case-grading
          v-show="isActiveStep('grading')"
          v-if="hasLoadedStep('grading')"
          :case-conf="state.caseConf"
          :readonly="!canWrite"
          @update="updateCase"
          @navigate="(category, params) => navigateToCategory(category, params)"
        />
        <div
          v-show="isActiveStep('caseLogic')"
          v-if="hasLoadedStep('caseLogic')"
          class="case-logic-container"
        >
          <div class="collapse-button-container">
            <md-button class="md-icon-button" @click="toggleSteps()">
              <md-icon>{{ state.stepsOpen ? 'first_page' : 'last_page' }}</md-icon>
            </md-button>
          </div>
          <case-logic
            :case-conf="state.caseConf"
            :user="user"
            :readonly="!canWrite"
            :navigation="state.navigation"
            :is-root="true"
            @update="updateCase"
          />
        </div>
      </div>
      <div v-show="!isInCaseEditor()" class="sub-panel">
        <keep-alive>
          <router-view
            :case-conf="state.caseConf"
            :readonly="!canWrite"
            @onBack="navigateToEditor"
            @updateCase="updateCase"
            @saving="subPanelSaving"
            @saved="subPanelSaved"
          />
        </keep-alive>
      </div>
      <div
        class="notifications"
        :style="{ 'background-color': isActiveStep('caseLogic') ? '#313136' : '#f3f3f3' }"
      >
        <div class="notification-buttons">
          <notification-menu
            v-show="showWarnings"
            :notifications="allWarnings"
            type="warning"
            :subtitle="'Some aspects of the scenario are non-functional due to a change or deletion'"
            @open="notification => openNotification(notification)"
            @dismiss="warning => dismissWarning(warning)"
          />
          <notification-menu
            v-show="showErrors"
            :notifications="allErrors"
            type="error"
            :subtitle="'Some aspect of the scenario has made it inoperable'"
            @open="notification => openNotification(notification)"
          />
          <div v-if="errorState === 'ERRORS_NOT_SAVED'" class="not-saved-indicator">
            <md-icon>notifications</md-icon>
            Not saved!
          </div>
        </div>
        <div class="actions">
          <md-button
            v-if="isActiveStep('ivs') || isActiveStep('medications')"
            :disabled="!caseConf || !caseConf.iv || !caseConf.iv.enabled"
            class="actions-button md-raised md-secondary-accent"
            @click="openOnAtStartPanel()"
          >
            On at Start
          </md-button>
          <md-button
            class="actions-button primary"
            :disabled="!canWrite"
            @click="onValidateClicked()"
          >
            Validate
          </md-button>
          <md-button
            class="actions-button primary"
            :disabled="!!saving || !!allErrors.length || !canRead || state.isNew"
            @click="run()"
          >
            Run
          </md-button>
          <save-button
            :saving="saving"
            :error-state="errorState"
            :is-new="state.isNew"
            :disabled="!canWrite"
            :dark="isActiveStep('caseLogic')"
            @save="manualSave"
          />
        </div>
      </div>
      <md-drawer
        v-if="isAdminOrInstructor"
        class="tutorial-drawer"
        :md-fixed="true"
        :md-active.sync="state.tutorialDrawerOpen"
        @md-closed="closeTutorialDrawer"
      >
        <tutorial-list
          v-if="state.tutorialDrawerOpen"
          :category="state.tutorialDrawerCategory"
          :product-type="user.productType"
          @close="closeTutorialDrawer"
        />
      </md-drawer>
    </div>
    <md-dialog-alert
      :md-active.sync="state.openedError"
      :md-content="state.openedError ? state.openedError.message + '<br>Contact VRpatients.' : ''"
      md-title="An unknown error occurred"
      md-confirm-text="Close"
      class="warning-dialog confirm-dialog"
    />
    <div class="old-globals">
      <on-at-start-drawer
        :case-conf="state.caseConf"
        :readonly="!canWrite"
        @updateInitialIvs="updateInitialIvs"
        @updateInitialMedications="updateInitialMedications"
      />
    </div>
  </div>
</template>

<script>
import {
  cloneDeep,
  assign,
  omitBy,
  pick,
  uniq,
  debounce,
  reduce,
  findIndex,
  isEmpty,
  get,
  keyBy,
  intersection,
  without,
} from 'lodash';
import isFunction from 'lodash/isFunction';

import diff from 'deep-diff';
import avatars from '~lib/common/config/avatars';
import validateCaseConf from '~lib/common/validators/validateCaseConf';

import MenuButton from '~lib/common/components/MenuButton.vue';
import TutorialList from '~lib/common/components/Tutorials/TutorialList.vue';

import CaseSummary from './overview/CaseSummary';
import CaseScenario from './overview/CaseScenario';
import CaseDispatch from './overview/CaseDispatch';
import CaseInitialConditions from './conditions/CaseInitialConditions';
import CaseMonitors from './monitors/CaseMonitors';
import CaseQuestionsContainer from './questions/CaseQuestions';
import CaseCustomMenus from './custommenus/CaseCustomMenus';
import CaseMedications from './treatments/CaseMedications';
import CaseIVs from './treatments/CaseIVs';
import CaseProcedures from './treatments/CaseProcedures';
import CaseOxygens from './oxygen/CaseOxygens';
import CaseInterventions from './interventions/CaseInterventions';
import CaseLabMedia from './labmedia/CaseLabMedia';
import CaseGrading from './grading/CaseGrading';
import CaseLogic from '../../lib/case-logic-builder/CaseLogic.vue';

import updateCaseConf from '../utils/updateCaseConf';
import getWebglLink from '../utils/getWebglLink';
import saveCaseConf from '../utils/saveCaseConf';
import Timeline from './common/Timeline.vue';
import NotificationMenu from './common/NotificationMenu';
import SaveButton from './common/SaveButton.vue';
import OnAtStartDrawer from './treatments/OnAtStartDrawer/OnAtStartDrawer.vue';

import caseFieldsToCategories from './caseFieldsToCategories.json';

const steps = [
  { key: 'scenario', label: 'Scenario', color: '#004580' },
  { key: 'dispatch', label: 'Dispatch Report', color: '#0f5398' },
  { key: 'patientConditions', label: 'Patient Conditions', color: '#286db3' },
  { key: 'dialogue', label: 'Dialogue', color: '#3f83c5' },
  { key: 'customMenus', label: 'Custom Menus', color: '#3fabd9' },
  { key: 'ivs', label: 'IV', color: '#60c1d8' },
  { key: 'medications', label: 'Medications/Fluids', color: '#80d6d6' },
  { key: 'oxygen', label: 'Oxygen', color: '#81d7ad' },
  { key: 'interventions', label: 'Interventions', color: '#82d784' },
  { key: 'procedures', label: 'Procedures', color: '#6cc66d' },
  { key: 'monitors', label: 'Monitors', color: '#56b456' },
  { key: 'labMedia', label: 'Media', color: '#4b9f4a' },
  { key: 'grading', label: 'Grading', color: '#40883f' },
  { key: 'caseLogic', label: 'Scenario logic', color: '#2f6e32' },
];

const stepLocations = {
  _avatar: 0,
  _startLocation: 0,
  name: 0,
  _labAssets: 9,
  caseLogicDescription: 13,
};

export default {
  name: 'case-build-assistant',
  components: {
    CaseSummary,
    MenuButton,
    CaseScenario,
    CaseDispatch,
    CaseInitialConditions,
    CaseQuestionsContainer,
    CaseCustomMenus,
    CaseMonitors,
    CaseMedications,
    'case-ivs': CaseIVs,
    CaseProcedures,
    CaseOxygens,
    CaseInterventions,
    CaseLabMedia,
    CaseGrading,
    Timeline,
    CaseLogic,
    NotificationMenu,
    SaveButton,
    OnAtStartDrawer,
    TutorialList,
  },
  props: {
    caseConf: {
      type: Object,
      required: false,
      default: undefined,
    },
    savingItemId: {
      type: String,
      default: undefined,
    },
    submitting: {
      type: Boolean,
    },
    printing: {
      type: Boolean,
    },
    exporting: {
      type: Boolean,
    },
    user: {
      type: Object,
      default: undefined,
    },
    submitErrors: {
      type: Array,
      default: () => [],
    },
    saveErrors: {
      type: Array,
      default: () => [],
    },
    fetchErrors: {
      type: Array,
      default: () => [],
    },
    voices: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const _avatar = this.getBenAvatar();
    const isMilitary = (this.user.productType === 'MILITARY');
    return {
      state: {
        activeStep: 0,
        loadedSteps: {
          scenario: true,
        },
        caseConf: {
          language: 'en',
          _avatar,
          name: '',
          voiceKey: this.getDefaultVoice(_avatar.key),
          outfit: {
            clothingType: isMilitary ? 'MILITARY_OUTFIT_1' : _avatar.avatarOptions.defaultOutfit,
            isDirty: false,
            onAtStart: 'CLOTHES_ON'
          },
          dispatch: {
            learningObjectives: {
              caption: {
                en: '',
              },
              displayed: false,
            },
            chiefComplaint: {
              caption: {
                en: '',
              },
              displayed: false,
            },
            goal: {
              caption: {
                en: '',
              },
              displayed: false,
            },
            scenarioScript: '',
            codeStatuses: null,
            certificationLevels: null,
          },
          _startLocation: null,
          _endLocation: null,
          _sounds: [],
          _oxygens: [],
          _treatments: [],
          _initialStates: [
            {
              id: 'temperature',
              options: {
                value: '98.6',
              },
            }
          ],
          completedStepsCount: 0,
          errorsCount: 0,
          warningsCount: 0,
          monitorMetadata: {
            monitorModel: 'ZOLL',
          },
          grading: null,
          caseLogicDescription: null,
          dialogue: {
            questions: null,
            commands: null,
            unsolicited: null,
            calls: null,
            useCurrentDate: true,
          },
          initialConditions: {
            disablePulseBpSync: false,
            disablePulseStrengthsMatching: false,
            pulses: [],
            height: null,
            weight: null,
          },
          moulages: null,
          structuralMoulages: null,
          spinalAssessments: null,
          tags: [],
          measurementSystem: null,
          temperatureUnits: null,
          iv: {
            enabled: false
          },
          dosageForms: [],
          fluids: [],
          customMedications: [],
          customFluids: [],
          initialIvs: [],
          initialMedications: [],
          customMenus: [],
          npcs: [],
        },
        lastSavedCaseConf: null,
        isNew: this.$route.params.id === 'new',
        isNewInitial: this.$route.params.id === 'new',
        subPanelSaving: false,
        navigation: {},
        stepsOpen: true,
        openedError: null,
        validating: false,
        destroyed: false,
        tutorialDrawerOpen: false,
        tutorialDrawerCategory: null,
      },
      steps,
      stepLocations,
      errorIndex: 0,
      warningIndex: 0,
      errorsHidden: false,
    };
  },
  computed: {
    environments() {
      const { caseConf } = this.state;
      return [caseConf._startLocation, caseConf._endLocation].filter(e => !!e);
    },
    showErrors() {
      return !this.errorsHidden && this.hasErrors;
    },
    showWarnings() {
      return this.hasWarnings;
    },
    saving() {
      return (
        this.submitting || !!this.savingItemId || this.state.subPanelSaving || this.state.validating
      );
    },
    hasErrors() {
      return !!this.errorsCount;
    },
    hasWarnings() {
      return !!this.warningsCount;
    },
    errors() {
      return this.$store.getters['validation/errorsByCategory'];
    },
    warnings() {
      return this.$store.getters['validation/visibleWarningsByCategory'];
    },
    errorsCount() {
      return this.$store.state.validation.errors.length;
    },
    warningsCount() {
      const count = reduce(
        this.warnings,
        (result, val) => {
          const length = val ? val.length : 0;
          return result + length;
        },
        0,
      );
      return count;
    },
    remoteErrors() {
      const remoteErrors = [].concat(this.fetchErrors, this.submitErrors, this.saveErrors);
      return remoteErrors;
    },
    allErrors() {
      return this.$store.state.validation.errors;
    },
    allWarnings() {
      return this.$store.getters['validation/allVisibleWarnings'] || [];
    },
    stepsWithErrors() {
      return steps.map((s, index) => ({
        ...s,
        hasError: this.allErrors.some(
          error => s.key === error.category || this.stepLocations[error.field] === index,
        ),
        hasWarning: this.allWarnings.some(
          warning => s.key === warning.category || this.stepLocations[warning.field] === index,
        ),
      }));
    },
    stepsByKey() {
      return keyBy(
        steps.map((step, index) => ({ ...step, index })),
        'key',
      );
    },
    canRead() {
      return this.hasPermission('read');
    },
    canWrite() {
      return this.hasPermission('write');
    },
    canShare() {
      return this.hasPermission('share');
    },
    isAdminOrInstructor() {
      return this.user.activeRole !== 'STUDENT';
    },
    errorState() {
      return this.$store.getters['validation/errorState'];
    },
  },
  watch: {
    remoteErrors: {
      immediate: true,
      handler(remoteErrors) {
        this.$store.commit('validation/concatErrors', remoteErrors);
      }
    }
  },
  async created() {
    this.state.caseConf = cloneDeep(
      assign(
        this.state.caseConf,
        omitBy(this.caseConf, v => v === undefined || v === null),
      ),
    );
    this.state.lastSavedCaseConf = cloneDeep(this.state.caseConf);
    this.debouncedSave = debounce(this.save, 3000, {
      leading: false,
      trailing: true,
    });
    if (this.state.caseConf.id) {
      await this.validateCase();
    }
    this.$eventHub.$on('CASE::FLUSH_PENDING_CHANGES', this.flushPendingChanges);
    if (this.state.caseConf.warningsHidden) {
      this.hideWarnings();
    }
  },
  destroyed() {
    this.state.destroyed = true;
    this.$store.commit('validation/reset');
    this.$eventHub.$off('CASE::FLUSH_PENDING_CHANGES', this.flushPendingChanges);
  },
  methods: {
    updateCase(updates) {
      if (this.canWrite) {
        this.state.caseConf = updateCaseConf(this.state.caseConf, updates);
        this.debouncedSave();
      }
    },
    async save(callback, saveAnyway) {
      try {
        const { caseConf, lastSavedCaseConf } = this.state;
        const lastSavedCaseConfCandidate = cloneDeep(caseConf);
        if (this.state.isNew && !caseConf.name) {
          return;
        }

        if (! await this.validateCase()) {
          if (!saveAnyway) {
            return;
          }
        }

        const caseDiff = diff(lastSavedCaseConf, caseConf);
        // Some fields are part of the received caseConf but not part
        // of the input
        const queryOnlyFields = ['outdatedEcNodes', 'outdatedMedications'];
        const dirtyFields = (caseDiff && without(uniq(caseDiff.map(edit => edit.path[0])), ...queryOnlyFields)) || [];

        if (!saveAnyway && (!dirtyFields || dirtyFields.length === 0)) {
          if (callback && isFunction(callback)) {
            callback(null, this.state.caseConf);
          }
          return;
        }

        // If the scenario name changed, then send the access field which is used
        // to check for name conflicts
        if (dirtyFields.includes('name')) {
          dirtyFields.push('access')
        }

        this.$emit('onSaveStart');
        this.$store.commit('validation/setSaving', true);

        const sentCaseConf = cloneDeep(this.state.isNew
          ? caseConf
          : pick(
              caseConf,
              ['id'].concat(dirtyFields)
            )
        );
        sentCaseConf.warningsCount = this.allWarnings.length;
        sentCaseConf.errorsCount = this.allErrors.length;
        // If the component has been destroyed before it had a chance
        // to save its last changes, we can no longer rely on vue emitting
        // our event to the parent component (dataView hoc), so we have
        // to save the case directly
        if (this.state.destroyed) {
          await saveCaseConf(sentCaseConf);
          this.$store.commit('validation/setSaving', false);
          return;
        }
        this.$emit(
          // If it's a new case that has not been created yet, add it first
          this.state.isNew ? 'add' : 'save',
          // id, access and warningsCount are always sent in the patch
          sentCaseConf,
          // Once it's added, update the state with the id of the newly created case
          newCase => {
            if (!caseConf.id) {
              caseConf.id = newCase.id;
              caseConf.access = newCase.access;
              this.state.isNew = false;
            }
            this.state.lastSavedCaseConf = lastSavedCaseConfCandidate;
            if (callback && isFunction(callback)) {
              callback(null, newCase);
            }
          }
        );
      } catch (e) {
        console.error(e);
        this.$store.commit('validation/concatErrors', [e]);
        if (callback && isFunction(callback)) {
          callback(e, null);
        }
      } finally {
        this.$emit('onSaveEnd');
        this.$store.commit('validation/setSaving', false);
      }
    },
    manualSave(saveAnyway) {
      this.save(null, saveAnyway);
    },
    flushPendingChanges() {
      // Wait for children components to flush
      // their pending changes, then save
      this.$nextTick(() => {
        this.debouncedSave.cancel();
        this.save();
      });
    },
    run() {
      const orgId = this.user.groups[0];
      const webglLink = getWebglLink(this.state.caseConf.id, orgId);
      if (this.canWrite) {
        this.save(() => {
          window.open(webglLink, '_blank');
        });
      } else {
          window.open(webglLink, '_blank');
      }
    },
    onValidateClicked() {
      this.errorsHidden = false;
      this.updateCase({ warningsHidden: false });
      this.validateCase();
      this.$store.commit('validation/showAllWarnings');
      this.$analytics.event('Authoring Tool', 'validate_case_manual', this.allWarnings.length ? this.allWarnings[0].message : undefined);
    },
    nextError() {
      if (!this.hasErrors) return;

      const errorCategories = uniq(this.allErrors.map(error => error.category));
      if (this.errorIndex >= errorCategories.length) {
        this.errorIndex = 0;
      }

      const nextCategory = errorCategories[this.errorIndex];

      this.navigateToStep(findIndex(steps, { key: nextCategory }));

      this.errorIndex = this.errorIndex + 1;
    },
    nextWarning() {
      if (!this.hasWarnings) return;

      const warningCategories = uniq(this.allWarnings.map(error => error.category));
      if (this.warningIndex >= warningCategories.length) {
        this.warningIndex = 0;
      }

      const nextCategory = warningCategories[this.warningIndex];

      this.navigateToStep(findIndex(steps, { key: nextCategory }));

      this.warningIndex = this.warningIndex + 1;
    },
    hideErrors() {
      this.errorsHidden = true;
    },
    hideWarnings() {
      this.updateCase({ warningsHidden: true });
      this.$store.commit('validation/showNoWarnings');
    },
    dismissWarning(warning) {
      this.$store.commit('validation/dismiss', warning);
    },
    close() {
      this.$emit('close');
    },
    navigateToStep(stepNumber, params) {
      if (!this.isInCaseEditor()) {
        this.navigateToEditor();
      }
      const step = steps[stepNumber];
      if (params) {
        // Pass error/warning
        this.state.navigation = { category: step.key, params };
      }
      this.setStep(stepNumber);
    },
    openNotification(notification) {
      if (notification.category) {
        this.navigateToCategory(notification.category, notification.params || notification.metadata);
      } else if (notification.field) {
        const category = caseFieldsToCategories[notification.field];
        this.navigateToCategory(category);
      } else {
        this.state.openedError = notification;
      }
    },
    navigateToCategory(category, params) {
      if (!category) {
        return;
      }
      this.navigateToStep(findIndex(steps, { key: category }), params);
    },
    setStep(stepNumber) {
      const overviewStepNumber = findIndex(steps, { key: 'scenario' });
      if (stepNumber > overviewStepNumber && !this.state.caseConf.id) {
        return;
      }
      const targetStep = steps[stepNumber];
      this.state.activeStep = stepNumber;
      this.state.loadedSteps[steps[stepNumber].key] = true;
      if (stepNumber > this.state.caseConf.completedStepsCount) {
        this.updateCase({ completedStepsCount: stepNumber });
      }
      this.$analytics.event('Authoring Tool', 'navigate', targetStep.label);
    },
    nextStep() {
      if (this.state.activeStep < steps.length - 1) {
        this.setStep(this.state.activeStep + 1);
      }
    },
    prevStep() {
      if (this.state.activeStep > 0) {
        this.setStep(this.state.activeStep - 1);
      }
    },
    isActiveStep(stepKey) {
      return (
        steps.findIndex(step => step.key === stepKey) === this.state.activeStep
      );
    },
    hasLoadedStep(stepKey) {
      return this.state.loadedSteps[stepKey];
    },
    isAtLastStep() {
      return this.state.activeStep === steps.length - 1;
    },
    toggleSteps() {
      this.state.stepsOpen = !this.state.stepsOpen;
    },
    navigateToEditor() {
      this.$router.go(-1);
    },
    isInCaseEditor() {
      return true;
    },
    isInSubRoute() {
      return false;
    },
    subPanelSaving() {
      this.state.subPanelSaving = true;
    },
    subPanelSaved() {
      this.state.subPanelSaving = false;
    },
    hasPermission(permission) {
      if (this.state.isNewInitial) {
        return true;
      }
      const userId = this.user.id;
      const userGroups = this.user.groups;
      const accessSpecification = get(
        this.caseConf,
        `access.${permission}`,
        {},
      );
      if (!accessSpecification) {
        return false;
      }
      return (
        (accessSpecification.users &&
          accessSpecification.users.includes(userId)) ||
        intersection(accessSpecification.groups, userGroups).length !== 0
      );
    },
    async validateCase() {
      this.state.validating = true;
      const validationResult = await validateCaseConf(this.state.caseConf);
      if (this.state.destroyed) {
        // validation has finished after case has been exited, don't commit to the store
        return isEmpty(validationResult.errors);
      }
      this.$store.commit('validation/updateErrors', validationResult.errors);
      this.$store.commit('validation/updateWarnings', validationResult.warnings);
      this.state.validating = false;
      if (!isEmpty(validationResult.errors)) {
        this.$analytics.event('Authoring Tool', 'validation_notification', this.allErrors.length ? this.allErrors[0].message : undefined);
      }
      return isEmpty(validationResult.errors);
    },
    openOnAtStartPanel() {
      const navigateObj = {
        category: steps[this.state.activeStep].key,
      };
      this.$eventHub.$emit('UI::OPEN_ON_AT_START_PANEL', navigateObj);
    },
    updateInitialIvs(initialIvs) {
      this.updateCase({ initialIvs });
    },
    updateInitialMedications(initialMedications) {
      this.updateCase({ initialMedications });
    },
    getBenAvatar() {
      const avatar = avatars.find(a => a.key === 'BEN');
      return {
        ...avatar,
        id: 'BEN', // won't save correctly without an id
      }
    },
    getDefaultVoice(avatarKey) {
      const voice = this.voices.find(voice => voice.avatarKey === avatarKey && voice.isDefault);
      if (voice) {
        return voice.id;
      }
    },
    showHelp() {
      window.open('https://vrpatients.atlassian.net/wiki/spaces/VP/overview', '_blank');
    },
    openTutorialDrawer(category) {
      this.state.tutorialDrawerCategory = category;
      this.state.tutorialDrawerOpen = true;
    },
    closeTutorialDrawer() {
      this.state.tutorialDrawerOpen = false;
    },
    hasAiIcon(stepKey) {
      const aiDialogEnabled = this.state.caseConf.dialogue && this.state.caseConf.dialogue.aiEnabled;
      const aiAssessmentEnabled = this.state.caseConf.grading && this.state.caseConf.grading.aiAssessmentEnabled;
      return (aiDialogEnabled && stepKey === 'dialogue') || (aiDialogEnabled && aiAssessmentEnabled && stepKey === 'grading');
    },
  },
};
</script>

<style scoped>
.container {
  height: 100%;
  display: flex;
}

.back-button {
  position: relative;
  z-index: 1;
  width: 100%;
}

.case-summary {
  position: relative;
  z-index: 2;
}

.steps-container,
.editor-container {
  height: 100%;
  box-sizing: border-box;
  float: left;
}

.steps-menu-buttons {
  display: flex;
  flex-direction: column;
  width: calc(100% - 30px);
  max-height: calc(100% - 250px);
  float: left;
}

.steps-container {
  position: relative;
  z-index: 4;
  min-width: 320px;
  max-width: 320px;
}

.editor-container {
  position: relative;
  width: calc(100% - 320px);
  min-width: 620px;
}

.case-build-assistant.steps-collapsed .editor-container {
  width: 100%;
}

.timeline {
  max-height: calc(100% - 250px);
}

.menu-button {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: center;
  transition: none;
}

.menu-button.active,
.menu-button:active {
  z-index: 11;
  width: calc(100% + 30px);
}

.steps-container {
  width: 25%;
  background-color: #303243;
}

.editor,
.sub-panel {
  height: calc(100% - 65px);
  overflow: auto;
}

.bottom-left-buttons {
  position: absolute;
  bottom: 4px;
  display: flex;
}

.help-button {
  margin-left: 8px !important;
}

.help-button .md-icon {
  color: white;
}

.scenario-builder-button,
.scenario-logic-button {
  width: 120px;
}

.guide-text {
  font-weight: normal;
  text-align: center;
  color: gray;
}

.notifications {
  position: absolute;
  right: 0px;
  bottom: 0px;
  height: 65px;
  width: 100%;
}

.notifications {
  display: flex;
  align-items: center;
}

.not-saved-indicator {
  color: #F2994A;
  font-size: 15px;
  font-weight: 500;
}

.not-saved-indicator .md-icon {
  color: #F2994A;
}

.notifications > .notification-buttons {
  flex: 1;
  display: flex;
  align-items: center;
}

.actions {
  float: right;
  display: flex;
  height: 100%;
  padding: 0 10px;
  align-items: center;
}

.actions-button {
  margin-left: 4px;
  margin-right: 4px;
  font-size: 15px;
  font-weight: 500;
  color: white;
  height: 40px;
  border-radius: 50px;
}

.actions-button.primary {
  background-color: #0C70B0;
}

.actions-button.secondary {
  background-color: #0f5398;
}

.actions p {
  color: red;
  float: left;
}

.actions-button {
  padding: 0 8px;
}

.md-progress-spinner.md-theme-default.md-accent .md-progress-spinner-circle {
  stroke: white;
}

.vital-signs {
  border-left: solid 1px #ddd;
}

.guide-text {
  margin-top: 0;
}

.el-card {
  margin: 0 5px;
}

.questions-container {
  height: 100%;
}

.case-logic-container {
  height: 100%;
}

.tutorial-drawer {
  min-width: 640px;
}
</style>

<style>
.actions-button > * > .md-button-content {
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  width: 100%;
  height: 100%;
}

.case-build-assistant .steps-menu-buttons .menu-button .md-ripple {
  height: auto;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding-left: 25px;
}

.case-logic-container {
  display: flex;
  flex-direction: row;
  align-items: stretch;
}

.collapse-button-container {
  background: #303143;
  color: white;
  border: solid 1px white;
  border-radius: 0;
}

.collapse-button-container .md-button {
  height: 100%;
  border-radius: 0;
  margin: 0;
}

.collapse-button-container .md-icon-button:not(.md-primary) .md-icon {
  color: white;
}
</style>
